import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends EzStaticClass
 * @description
 * A class that provides static methods for working with JavaScript Map objects.
 * This class is not meant to be instantiated, as it only contains static methods.
 * @example
 * import { EzMap } from '/ezlibrary/helpers/EzMap.js';
 * const myMap = new Map([['key1', 'value1'], ['key2', 'value2']]);
 * const keysArray = EzMap.keysAsArray(myMap); // ['key1', 'key2']
 * const valuesArray = EzMap.valuesAsArray(myMap); // ['value1', 'value2']
 * const isValidMap = EzMap.isMap(myMap); // true
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *     import {
 *         // ... other helper classes ...,
 *         EzMap
 *     } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes (that allow it) with:
 *     import { EzMap } from '/ezlibrary/helpers/EzMap.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access:
 *     EzMap.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzMap extends EzStaticClass {
    /**
     * @static
     * @public @property
     * Gets a new empty Map instance.
     * @returns {Map}
     */
    static get EMPTY() {
        return new Map();
    }

    /**
     * @static
     * @public @function
     * Determines if the provided aMap is a valid Map instance and has a length greater than zero.
     * @param {undefined|null|Map} aMap
     * @returns {boolean}
     */
    static hasLength(aMap = null) {
        return EzMap.isMap(aMap) && aMap.size > 0;
    }

    /**
     * @static
     * @public @function
     * Determines if the provided aMap is a valid Javascript Map instance.
     * @param {undefined|null|Map} aMap
     * @returns {boolean}
     */
    static isMap(aMap = null) {
        return EzObject.isValid(aMap) && '[object Map]' === Object.prototype.toString.call(aMap);
    }

    /**
     * @static
     * @public @function
     * Gets the keys of the map as an array.
     * @param {Map} aMap
     * @return {Array}
     */
    static keysAsArray(aMap = null) {
        if (!EzMap.isMap(aMap)) {
            throw new EzBadParamException(
                {aMap},
                this,
                this.valuesAsArray);
        }

        return EzMap.hasLength(aMap)
            ? aMap.keys().toArray()
            : EzArray.EMPTY;
    }

    /**
     * @static
     * @public @function
     * Gets the values of the map as an array.
     * @param {Map} aMap
     * @return {Array}
     */
    static valuesAsArray(aMap = null) {
        if (!EzMap.isMap(aMap)) {
            throw new EzBadParamException(
                {aMap},
                this,
                this.valuesAsArray);
        }

        return aMap.values().toArray();
    }

    /**
     * @static
     * @public @function
     * Transforms the provided jsonObject to a Map instance.
     * @param {undefined|null|object} jsonObject}
     * @returns {Map}
     */
    static jsonObjectToMap(jsonObject) {
        if (!EzObject.isValid(jsonObject)) {
            throw new EzBadParamException(
                { jsonObject },
                this,
                this.jsonObjectToMap);
        }

        const newMap = new Map();

        for (const [key, value] of Object.entries(jsonObject)) {
            newMap.set(key, value);
        }

        return newMap;
    }

    static mapOrDefault(aMap, aDefault) {
        return EzMap.isMap(aMap)
            ? aMap
            : aDefault;
    }
}